/**
 * Cookie consent management utility
 * Handles user preferences for different cookie categories
 */

export type CookieCategory = 'essential' | 'analytics' | 'marketing'

export interface CookiePreferences {
  essential: boolean // Always true, cannot be disabled
  analytics: boolean
  marketing: boolean
  timestamp: number // When preferences were saved
}

export interface CookieConsentStatus {
  hasConsented: boolean
  preferences: CookiePreferences | null
  isExpired: boolean
}

/**
 * Cookie consent manager class
 */
export class CookieManager {
  private static readonly STORAGE_KEY = 'cookie-preferences'
  private static readonly CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000 // 1 year in milliseconds

  /**
   * Check if user has given cookie consent
   */
  static getConsentStatus(): CookieConsentStatus {
    if (typeof window === 'undefined') {
      return {
        hasConsented: false,
        preferences: null,
        isExpired: true
      }
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) {
        return {
          hasConsented: false,
          preferences: null,
          isExpired: true
        }
      }

      const preferences: CookiePreferences = JSON.parse(stored)
      const isExpired = Date.now() - preferences.timestamp > this.CONSENT_DURATION

      return {
        hasConsented: true,
        preferences,
        isExpired
      }
    } catch (error) {
      console.error('Error reading cookie preferences:', error)
      return {
        hasConsented: false,
        preferences: null,
        isExpired: true
      }
    }
  }

  /**
   * Save user cookie preferences
   */
  static savePreferences(preferences: Omit<CookiePreferences, 'timestamp' | 'essential'>): void {
    if (typeof window === 'undefined') return

    const fullPreferences: CookiePreferences = {
      essential: true, // Always true
      analytics: preferences.analytics,
      marketing: preferences.marketing,
      timestamp: Date.now()
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(fullPreferences))
      
      // Fire custom event for other components to listen
      window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', {
        detail: fullPreferences
      }))
    } catch (error) {
      console.error('Error saving cookie preferences:', error)
    }
  }

  /**
   * Accept all cookies
   */
  static acceptAll(): void {
    this.savePreferences({
      analytics: true,
      marketing: true
    })
  }

  /**
   * Accept only essential cookies
   */
  static acceptEssentialOnly(): void {
    this.savePreferences({
      analytics: false,
      marketing: false
    })
  }

  /**
   * Decline all non-essential cookies
   */
  static declineAll(): void {
    this.acceptEssentialOnly()
  }

  /**
   * Clear all cookie preferences (force re-consent)
   */
  static clearPreferences(): void {
    if (typeof window === 'undefined') return

    try {
      localStorage.removeItem(this.STORAGE_KEY)
      
      // Fire custom event
      window.dispatchEvent(new CustomEvent('cookiePreferencesCleared'))
    } catch (error) {
      console.error('Error clearing cookie preferences:', error)
    }
  }

  /**
   * Check if specific cookie category is allowed
   */
  static isCategoryAllowed(category: CookieCategory): boolean {
    const { preferences } = this.getConsentStatus()
    
    if (!preferences) {
      // No consent given - only essential cookies allowed
      return category === 'essential'
    }

    return preferences[category]
  }

  /**
   * Check if analytics cookies are allowed
   */
  static isAnalyticsAllowed(): boolean {
    return this.isCategoryAllowed('analytics')
  }

  /**
   * Check if marketing cookies are allowed
   */
  static isMarketingAllowed(): boolean {
    return this.isCategoryAllowed('marketing')
  }

  /**
   * Get current preferences for display
   */
  static getCurrentPreferences(): CookiePreferences | null {
    const { preferences } = this.getConsentStatus()
    return preferences
  }

  /**
   * Check if consent banner should be shown
   */
  static shouldShowConsentBanner(): boolean {
    const { hasConsented, isExpired } = this.getConsentStatus()
    return !hasConsented || isExpired
  }

  /**
   * Add event listener for preference changes
   */
  static onPreferencesChange(callback: (preferences: CookiePreferences) => void): () => void {
    if (typeof window === 'undefined') {
      return () => {} // Return empty cleanup function
    }

    const handler = (event: CustomEvent<CookiePreferences>) => {
      callback(event.detail)
    }

    window.addEventListener('cookiePreferencesChanged', handler as EventListener)

    // Return cleanup function
    return () => {
      window.removeEventListener('cookiePreferencesChanged', handler as EventListener)
    }
  }

  /**
   * Add event listener for preferences cleared
   */
  static onPreferencesCleared(callback: () => void): () => void {
    if (typeof window === 'undefined') {
      return () => {} // Return empty cleanup function
    }

    window.addEventListener('cookiePreferencesCleared', callback)

    // Return cleanup function
    return () => {
      window.removeEventListener('cookiePreferencesCleared', callback)
    }
  }
}

/**
 * Hook for React components to use cookie preferences
 */
export function useCookiePreferences() {
  if (typeof window === 'undefined') {
    return {
      hasConsented: false,
      preferences: null,
      shouldShowBanner: true,
      acceptAll: () => {},
      acceptEssentialOnly: () => {},
      declineAll: () => {},
      clearPreferences: () => {},
      isAnalyticsAllowed: false,
      isMarketingAllowed: false
    }
  }

  const status = CookieManager.getConsentStatus()

  return {
    hasConsented: status.hasConsented && !status.isExpired,
    preferences: status.preferences,
    shouldShowBanner: CookieManager.shouldShowConsentBanner(),
    acceptAll: CookieManager.acceptAll.bind(CookieManager),
    acceptEssentialOnly: CookieManager.acceptEssentialOnly.bind(CookieManager),
    declineAll: CookieManager.declineAll.bind(CookieManager),
    clearPreferences: CookieManager.clearPreferences.bind(CookieManager),
    isAnalyticsAllowed: CookieManager.isAnalyticsAllowed(),
    isMarketingAllowed: CookieManager.isMarketingAllowed()
  }
}

/**
 * Default cookie preferences for new users
 */
export const DEFAULT_COOKIE_PREFERENCES: Omit<CookiePreferences, 'timestamp'> = {
  essential: true,
  analytics: false,
  marketing: false
}